import i18n from '@/plugins/i18n'
import { normalize } from '@/helpers/i18n.helpers'
import { PATIENT_ROLE, PROFESSIONAL_ROLE, CENTER_ROLE, ANONYMOUS_ROLE } from '@/constants/roles'
import { PRIVATE_LAYOUT, PUBLIC_REDUCED_LAYOUT, BACK_BTN_LAYOUT } from '@/constants/layouts'
import routeHelpers from '@/router/routeHelpers'
import store from '@/store'

// /patient/*

let routes = [
  {
    path: '/',
    name: 'Panel',
    component: () => import('@/components/webapp/patient/Panel'),
    meta: {
      title: {
        text () {
          return i18n.t('patient-panel.title')
        },
        hide: true
      },
      i18n: 'public/views/patient/panel'
    }
  },
  {
    path: 'sso-tmc',
    name: 'SsoTMC',
    component: () => import('@/components/webapp/patient/health/TMCQrCodeContainer'),
    meta: {
      layout: PUBLIC_REDUCED_LAYOUT,
      title: {
        text () {
          return i18n.t('telemedcare-account-association')
        }
      },
      logo: {
        src: 'opensalud360_healthierlives_color.svg'
      }
    }
  },
  {
    path: 'menttum',
    name: 'MenttumProxyView',
    component: () => import('@/components/webapp/patient/external/MenttumProxyView'),
    meta: {
      layout: PUBLIC_REDUCED_LAYOUT,
      layoutCentered: true,
      logo: {
        src: 'logo-menttum-grande.png'
      },
      title: {
        htmlTitle: 'Menttum',
        styleClasses: 'text-center'
      }
    }
  },
  {
    path: 'clinic-history',
    component: {
      template: '<router-view></router-view>'
    },
    meta: {
		// Determines if children meta properties are inherited from parent route (default: true)
		// Possible values are true (all), false or an array of property names to inherit
		// Example: bequeath: ['roles', 'i18n'], bequeath: true, bequeath: false
		// If meta property is defined in both parent and child, child property will be used (i18n will be always mixed)
		bequeath: true,
      	showTabMenu: true,
		roles: [PROFESSIONAL_ROLE, CENTER_ROLE, ANONYMOUS_ROLE],
		i18n: 'clinic-history',
		layout: window.location !== window.parent.location ? BACK_BTN_LAYOUT : PRIVATE_LAYOUT
    },
    children: [
      {
        path: '/',
        name: 'clinicHistoryMenu',
        component: () => import('@/components/views/clinicHistory/ClinicHistoryMenu'),
		meta: {
			title: {
				text () {
					return i18n.t('healthData')
				}
			}
		}
      },
      {
        path: 'view',
        name: 'clinicHistoryView',
        component: () => import('@/components/views/clinicHistory/ClinicHistoryView'),
		meta: {
			title: {
				text () {
					return i18n.t('clinichistory')
				}
			}
		}
      },
      {
        path: 'documents',
        component: () => import('@/components/views/clinicHistory/ClinicHistoryFiles'),
		meta: {
			title: {
				text () {
					return i18n.t('documentsAndInformation')
				}
			}
		}
      },
      {
        path: 'health-data',
        component: () => import('@/components/views/clinicHistory/ClinicHistoryHealthData'),
		meta: {
			title: {
				text () {
					return i18n.t('healthData')
				}
			},
			i18n: 'public/views/selfEvaluationTests.json'
		}
      },
      {
        path: 'menstrual-calendar',
        component: () => import('@/components/views/clinicHistory/MenstrualCalendar'),
		meta: {
			title: {
				text () {
					return i18n.t('menstrual-calendar')
				}
			}
		}
      },
	  {
        path: 'medication-reminder/new',
		name: 'newMedicationReminder',
        component: () => import('@/components/views/clinicHistory/medicationReminder/MedicationReminderView'),
				meta: {
					title: {
						text () {
							return i18n.t('newMedicationReminder')
						}
					}
				}
      },
	  {
        path: 'medication-reminder/edit/:id',
		name: 'editMedicationReminder',
        component: () => import('@/components/views/clinicHistory/medicationReminder/MedicationReminderView'),
				meta: {
					title: {
						text () {
							return i18n.t('editMedicationReminder')
						}
					}
				}
      },
	  {
        path: 'medication-reminder/:code?',
		name: 'medicationReminder',
        component: () => import('@/components/views/clinicHistory/medicationReminder/MedicationReminderView'),
		meta: {
			title: {
				text () {
					return i18n.t('medicationReminder')
				}
			}
		}
      },
      {
        path: 'quiz',
        name: 'clinicHistoryQuiz',
        component: () => import('@/components/views/clinicHistory/quiz/ClinicHistoryQuiz'),
		meta: {
			title: {
				text () {
					return i18n.t('healthQuiz')
				}
			}
		}
      }
    ]
  },
  {
    path: 'search',
    component: {
      template: '<router-view></router-view>'
    },
    meta: {
      showTabMenu: true,
      bequeath: true,
			title: {
				text () {
					return i18n.t('searcherTitle')
				},
				hide: true
			},
			i18n: 'public/views/patient/search'
    },
    children: [
      {
        path: '/',
        name: 'search',
        component: () => import('@/components/webapp/patient/search/Search')
      },
      {
        path: 'services',
        name: 'searchServices',
        component: () => import('@/components/webapp/patient/search/services/SearchServices')
      }
    ]
  },
  {
	path: 'appointments',
	component: {
		template: '<router-view></router-view>'
	},
	meta: {
		showTabMenu: true,
		bequeath: true,
		title: {
			text () {
				return i18n.t('appointmentsTitle')
			},
			hide: true
		},
		i18n: ['public/views/patient/appointments', 'public/views/patient/panel']
	},
	children: [
		{
		  path: '/',
		  name: 'appointmentsMenu',
		  component: () => import('@/components/webapp/patient/AppointmentsPanelMenu'),
			title: {
				hide: true
			}
		},
		{
			path: 'next',
			name: 'nextAppointments',
			component: () => import('@/components/webapp/patient/appointments/NextAppointments')
		},
		{
			path: 'past',
			name: 'pastAppointments',
			component: () => import('@/components/webapp/patient/appointments/PastAppointments'),
			props: true
		}
	  ]
  },
  {
    path: 'tests',
		component: {
      template: '<router-view></router-view>'
    },
    meta: {
			bequeath: true,
			roles: [PATIENT_ROLE, PROFESSIONAL_ROLE],
			i18n: ['public/views/selfEvaluationTests.json', 'public/views/patient/panel', 'clinic-history']
		},
		children: [
			{
				path: '/',
				name: 'Tests',
				component: () => import('@/components/webapp/patient/SelfEvaluationTestMenu'),
				meta: {
					title: {
						text () {
							return i18n.t('tests.self-evaluation-tests.title')
						},
					},
					hide: true
				}
			},
			{
				path: 'opensalud-scorecard',
				name: 'OpensaludScorecard',
				component: () => import('@/components/views/selfEvaluationTests/OpensaludScorecard'),
				meta: {
					title: {
						text () {
							return 'opensalud ScoreCard'
						}
					},
					only360Users: true
				}
			},
			{
				path: ':testId/:patientId?',
				name: 'do-test',
				component: () => import('@/components/views/selfEvaluationTests/TestLoader'),
				meta: {
					title: {
						hide: true
					}
				}
			}
		]
  },
	{
		path: 'appointments-menu',
		name: 'AppointmentsMenu',
		component: () => import('@/components/webapp/patient/AppointmentsPanelMenu'),
		meta: {
			title: {
				text () {
					return i18n.t('patient-panel.teleconsultation')
				},
				hide: true
			},
			i18n: 'public/views/patient/panel'
		}
	},
	{
		path: 'goals-habits-menu',
		name: 'GoalsHabitsMenu',
		component: () => import('@/components/webapp/patient/GoalsHabitsPanelMenu'),
		meta: {
			title: {
				text () {
					return i18n.t('patient-panel.habits-challenges')
				},
				hide: true
			},
			i18n: 'public/views/patient/panel'
		}
	},
	{
		path: 'panel-search',
		name: 'Panel-search',
		component: () => import('@/components/webapp/patient/PanelSearch'),
		meta: {
		title: {
					text () {
						return i18n.t('patient-panel.teleconsultation')
					},
					hide: true
		},
		i18n: 'public/views/patient/panel'
		}
	},
	{
		path: 'panel-search-selector',
		name: 'Panel-search-selector',
		component: () => import('@/components/webapp/patient/PanelSearchSelector'),
		meta: {
		  title: {
					text () {
						return i18n.t('patient-panel.teleconsultation')
					},
					hide: true
		  },
		  i18n: 'public/views/patient/panel'
		}
	  },
	{
		path: 'test-health',
		name: 'TestHealth',
		component: () => import('@/components/views/TestPaciente'),
		meta: {
			showTabMenu: true,
			title: {
				text: 'Test Health',
				hide: true
			}
			/* i18n: 'public/views/test-health.json' */
		}
	},
	{
		path: 'healthy-food-menu',
		name: 'FoodHabitsMenu',
		component: () => import('@/components/webapp/patient/HealthyFoodPanelMenu'),
		meta: {
			title: {
				text () {
					return i18n.t('patient-panel.foods')
				},
				hide: true
			},
			i18n: 'public/views/patient/panel'
		}
	},
	// Microhabits
	{
    path: 'microhabits',
		component: {
      template: '<router-view></router-view>'
    },
    meta: {
			bequeath: true,
			i18n: ['public/views/patient/microhabits.json', 'public/views/healthy-habits', 'public/views/patient/panel.json']
		},
		children: [
			{
				path: '/',
				name: 'MicrohabitsPanelMenu',
				component: () => import('@/components/webapp/patient/microhabits/SimpleMicrohabitCategoriesPanelMenu'),
				meta: {
					title: {
						text () {
							return i18n.t('patient-panel.habits-that-care')
						},
					},
					hide: true
				}
			},
			{
				path: 'favorites',
				name: 'FavoritesMicrohabit',
				component: () => import('@/components/webapp/patient/microhabits/PatientSimpleMicrohabitsPanelMenu'),
				meta: {
					title: {
						text () {
							return i18n.t('patient-panel.my-microhabits')
						},
						hide: true
					},
					only360Users: true
				}
			},
			{
				path: ':category',
				name: 'SimpleMicrohabitsPanelMenu',
				component: () => import('@/components/webapp/patient/microhabits/SimpleMicrohabitsPanelMenu'),
				meta: {
					title: {
						text () {
							return `${store.state.microhabits.currentCategory ? `${normalize(store.state.microhabits.currentCategory.name)} |` : ''} ${i18n.t('patient-panel.habits-that-care')}`
						},
						hide: true
					}
				}
			}
		]
  },
	// Foods
	{
    path: 'foods',
	component: {
      template: '<router-view></router-view>'
    },
    meta: {
			bequeath: true,
			i18n: ['public/views/patient/foods.json', 'public/views/patient/panel.json', 'public/views/patient/microhabits.json']
		},
		children: [
			{
				path: '/',
				name: 'FoodsPanelMenu',
				component: () => import('@/components/webapp/patient/foods/FoodCategoriesPanelMenu'),
				meta: {
					title: {
						text () {
							return i18n.t('patient-panel.foods')
						},
					},
					hide: true,
					i18n: ['public/views/healthy-habits']
				}
			},
			{
				path: 'favorites',
				name: 'FavoritesFood',
				component: () => import('@/components/webapp/patient/foods/PatientFoodsPanelMenu'),
				meta: {
					title: {
						text () {
							return i18n.t('patient-panel.my-foods')
						},
						hide: true
					},
					only360Users: true
				}
			},
			{
				path: ':category',
				name: 'FoodCategoriesPanelMenu',
				component: () => import('@/components/webapp/patient/foods/FoodsPanelMenu'),
				meta: {
					title: {
						text () {
							return `${store.state.microhabits.currentCategory ? `${normalize(store.state.microhabits.currentCategory.name)} |` : ''} ${i18n.t('patient-panel.foods')}`
						},
						hide: true
					}
				}
			},
			{
				path: ':category/:food',
				name: 'FoodDetail',
				component: () => import('@/components/webapp/patient/foods/FoodDetail'),
				meta: {
					title: {
						text () {
							return `${store.state.microhabits.currentMicrohabit ? `${normalize(store.state.microhabits.currentMicrohabit.name)} |` : ''} ${i18n.t('patient-panel.foods')}`
						},
						hide: true
					},
					i18n: ['public/views/healthy-habits']
				}
			}
		]
  	},
	{
		path: 'my-apps',
		name: 'MyApps',
		component: () => import('@/components/webapp/patient/MyApplicationsPanelMenu'),
		meta: {
			title: {
				text () {
					return i18n.t('patient-panel.my-apps')
				},
				hide: false
			},
			i18n: ['public/views/patient/panel']
		}
	},
	{
		path: 'chats',
		component: {
			template: '<router-view></router-view>'
		},
		meta: {
			layout: PRIVATE_LAYOUT,
			showTabMenu: true,
			bequeath: true,
			i18n: ['public/views/shared/chat', 'public/views/patient/appointments'],
			title: {
				text () {
					return i18n.t('chats.chat')
				},
				hide: true
			}
		},
		children: [
			{
				path: '/',
				name: 'Chat',
				component: () => import('@/components/views/chat-interconsultations/Chat')
			},
			{
				path: 'showChat/:appointmentId',
				name: 'PastChat',
				component: () => import('@/components/views/chat-interconsultations/Chat')
			}
		]
	}
]

// Add meta roles patient and private layout to all routes
export default routeHelpers.completeRoutesMeta(routes, PATIENT_ROLE, PRIVATE_LAYOUT)



// WEBPACK FOOTER //
// ./src/router/routes/webapp/patient.routes.js